import type { FeatureCollection } from 'geojson'
import type * as maplibregl from 'maplibre-gl'
import { useCallback, useEffect, useRef, useState } from 'react'
import { buildGeonetWithinExpression } from './geonet-clip'
import {
  getLayerConfigs,
  LAYER_CONFIGS,
  type LayerConfig,
  ODD_TOGGLE_GROUPS,
  ODD_TOGGLE_TYPES,
  ODD_TYPE_TO_ELEMENT_TYPE,
} from './layer-config'
import {
  type OverwatchMapOddElementsPagedQueryVariables,
  useOverwatchMapListTaxonomiesLazyQuery,
  useOverwatchMapOddElementsPagedLazyQuery,
} from './MapOverlays.hook'
import {
  addFillLayer,
  addGeoJsonLaneLowLayer,
  addLineLayer,
  addOddSymbolLayer,
  addVectorFillLayer,
  addVectorLineLayer,
  applyLaneGeonetClip,
  geonetFillOpacity,
  LANE_PMTILES_MIN_ZOOM,
  laneArrowLayerId,
  laneLowLineId,
  oddArrowLayerId,
  oddLayerId,
  oddSourceId,
  removeMapLayer,
  removeOddLayer,
  setOddLayerOpacity,
} from './map-layers'
import { fetchAllOddElements } from './odd-elements-paging'
import {
  buildOddFeatureCollection,
  type OddLayerElement,
  type RouteOddElement,
} from './odd-layer-data'
import { filterTaxonomiesByType } from './taxonomy-utils'
import { OPERATING_AREA_OUTLINE_LAYER_ID } from './useOperatingAreasOverlay'

type UseMapOverlaysOptions = {
  map: maplibregl.Map | null
  metropolisId: number
  /** Human-readable name used to resolve layer configs when the numeric id isn't recognised. */
  metropolisName?: string
  /** Initial icon opacity for ODD layers (default 1). Route builder sets 0.15 when a route exists. */
  oddIconOpacity?: number
  /** Layer ID to insert overlays before (keeps overlays below this layer). */
  insertBeforeLayer?: string
  /** Auto-enable all geonet layers for this metro when the map first becomes ready */
  autoEnableGeonets?: boolean
  /** Auto-enable all lane layers for this metro when the map first becomes ready */
  autoEnableLanes?: boolean
  /**
   * Draw geonets as outlines without a fill. Used while the live operating area
   * is shown, so a hardcoded geonet doesn't tint it.
   */
  geonetOutlineOnly?: boolean
  /**
   * Elements that are on the active route. When provided, those elements get
   * renderFullOpacity=true (full opacity) while others are dimmed.
   */
  routeOddElements?: RouteOddElement[]
}

const EMPTY_ROUTE_ODD_ELEMENTS: RouteOddElement[] = []

export function useMapOverlays({
  map,
  metropolisId,
  metropolisName,
  oddIconOpacity = 1,
  insertBeforeLayer,
  autoEnableGeonets = false,
  autoEnableLanes = false,
  geonetOutlineOnly = false,
  routeOddElements = EMPTY_ROUTE_ODD_ELEMENTS,
}: UseMapOverlaysOptions) {
  const [expanded, setExpanded] = useState({
    geonets: false,
    lanes: false,
    odd: true,
  })
  // Which ODD sub-sections (group titles) are expanded. All start expanded.
  const initialGroupExpanded = Object.fromEntries(
    ODD_TOGGLE_GROUPS.map(g => [g.section, true]),
  )
  const [oddGroupExpanded, setOddGroupExpanded] =
    useState<Record<string, boolean>>(initialGroupExpanded)
  const [visible, setVisible] = useState<Record<string, boolean>>({})
  const [oddCounts, setOddCounts] = useState<Record<string, number>>({})
  const [oddLoading, setOddLoading] = useState<Record<string, boolean>>({})
  const [layerLoading, setLayerLoading] = useState<Record<string, boolean>>({})
  const oddLayerElementsRef = useRef<Record<string, OddLayerElement[]>>({})
  // Supersession tracking for async ODD toggles: each call takes the next global
  // sequence and records it as its type's current request, so a slow request can
  // tell a newer toggle replaced it and skip clobbering the new state. The source
  // is global and never reset — so no value is reused even across a metro switch,
  // which just clears the current-request map to invalidate in-flight requests.
  const oddRequestSeqRef = useRef(0)
  const oddCurrentRequestRef = useRef<Record<string, number>>({})
  // Per-type promise chain that serializes map installs. addOddSymbolLayer awaits
  // icon loading before mutating the shared per-type source/layers, so two requests
  // for one type must not interleave — otherwise the slower one's GeoJSON can land
  // on top of the newer result. Each request chains after the previous for its type.
  const oddInstallChainRef = useRef<Record<string, Promise<void>>>({})
  // Cache of each layer's loaded GeoJSON. A ref (not state) because the only
  // reader is the geonet `within` clip, which runs synchronously the moment a
  // geonet is added — before a state update would commit — and nothing in the
  // render output depends on it.
  const loadedDataRef = useRef<Record<string, FeatureCollection>>({})
  // A layer's GeoJSON once it has loaded. Its `layerLoading` entry turns false
  // after the data is stored, so callers re-render in time to read it.
  const getLoadedLayerData = useCallback(
    (id: string): FeatureCollection | undefined => loadedDataRef.current[id],
    [],
  )
  const [zoomLevel, setZoomLevel] = useState(13)

  const [fetchTaxonomies] = useOverwatchMapListTaxonomiesLazyQuery()
  const [fetchOddPaged] = useOverwatchMapOddElementsPagedLazyQuery()

  const taxonomiesRef = useRef<string[]>([])
  const activeOddLayersRef = useRef(new Set<string>())
  // Tracks PMTiles-backed layers currently on the map, keyed by their vector
  // source id, so the map 'error' handler can fall back to the layer's GeoJSON.
  const pmtilesFallbackRef = useRef(new Map<string, LayerConfig>())
  // Layer ids whose PMTiles source has errored — once failed we use GeoJSON.
  const failedPMTilesRef = useRef(new Set<string>())
  // Types the active route turned on (not the user). This set is the sole owner
  // of route-driven ODDs: they're turned off as soon as they drop off the route
  // (see syncRouteOddTypes). Any other visible ODD is a manual selection and is
  // therefore left untouched by route changes.
  const autoEnabledOddRef = useRef(new Set<string>())
  const routeOddElementsRef = useRef(routeOddElements)
  routeOddElementsRef.current = routeOddElements
  const visibleRef = useRef(visible)
  visibleRef.current = visible
  // Read by the geonet render so toggling the option doesn't recreate callbacks.
  const geonetOutlineOnlyRef = useRef(geonetOutlineOnly)
  geonetOutlineOnlyRef.current = geonetOutlineOnly
  const prevMetroRef = useRef(metropolisId)
  const metropolisIdRef = useRef(metropolisId)
  metropolisIdRef.current = metropolisId
  const oddIconOpacityRef = useRef(oddIconOpacity)
  oddIconOpacityRef.current = oddIconOpacity
  // Latest map instance, so an in-flight toggle can tell its captured map was
  // replaced and avoid installing a layer on the old/detached one.
  const latestMapRef = useRef(map)
  latestMapRef.current = map

  const setLayerVisible = useCallback((key: string, checked: boolean) => {
    visibleRef.current = { ...visibleRef.current, [key]: checked }
    setVisible(prev => ({ ...prev, [key]: checked }))
  }, [])

  useEffect(() => {
    if (!map) return
    const onZoom = () => setZoomLevel(map.getZoom())
    map.on('zoom', onZoom)
    setZoomLevel(map.getZoom())
    return () => {
      map.off('zoom', onZoom)
    }
  }, [map])

  useEffect(() => {
    fetchTaxonomies({ variables: { metroId: metropolisId } }).then(result => {
      taxonomiesRef.current = result.data?.taxonomies ?? []
    })
  }, [metropolisId, fetchTaxonomies])

  useEffect(() => {
    if (prevMetroRef.current === metropolisId) return
    prevMetroRef.current = metropolisId

    if (map) {
      for (const [key, isOn] of Object.entries(visibleRef.current)) {
        if (!isOn) continue
        if (key.startsWith('odd-')) continue
        // The visible layer may belong to any previously-active metro, so search all known configs.
        const config = Object.values(LAYER_CONFIGS)
          .flat()
          .find(c => c.id === key)
        if (config) {
          removeMapLayer(map, key, config.type === 'geonet' ? 'fill' : 'line')
        }
      }
      for (const type of activeOddLayersRef.current) {
        removeOddLayer(map, type)
      }
      activeOddLayersRef.current.clear()
    }
    // Drop PMTiles fallback tracking; failed-layer memory is metro-scoped so a
    // new metro re-attempts PMTiles even if a previous metro's layer failed.
    pmtilesFallbackRef.current.clear()
    failedPMTilesRef.current.clear()
    visibleRef.current = {}
    setVisible({})
    setOddCounts({})
    oddLayerElementsRef.current = {}
    // Invalidate any in-flight request from the previous metro: with its type no
    // longer in the map, isCurrent() is false. Safe to clear because the sequence
    // source (oddRequestSeqRef) keeps climbing, so a new request can't reuse an old
    // request's sequence and pass isCurrent() by accident.
    oddCurrentRequestRef.current = {}
    // Clear ODD loading too: an in-flight request invalidated just above will skip
    // its isCurrent()-guarded setOddLoading(false), so without this a type not
    // re-enabled in the new metro would stay stuck loading.
    setOddLoading({})
    loadedDataRef.current = {}
    setLayerLoading({})
    // Clear one-shot guards so auto-enable fires fresh for the new metro.
    autoEnabledKeysRef.current.clear()
    // ODD categorisation is metro-specific — drop it so route-driven state from
    // the previous metro can't leak into the new one.
    autoEnabledOddRef.current.clear()
  }, [metropolisId, map])

  const toggleSection = useCallback(
    (section: 'geonets' | 'lanes' | 'odd') =>
      setExpanded(prev => ({ ...prev, [section]: !prev[section] })),
    [],
  )

  const beforeLayer = useCallback(
    (m: maplibregl.Map) =>
      insertBeforeLayer && m.getLayer(insertBeforeLayer)
        ? insertBeforeLayer
        : undefined,
    [insertBeforeLayer],
  )

  // Enforce z-order: geonet fills → lane lines → geonet outlines → ODD layers.
  // Must run after every layer add because MapLibre inserts at a fixed position
  // but doesn't reorder existing layers.
  const applyLayerZOrder = useCallback(
    (m: maplibregl.Map) => {
      const before = beforeLayer(m)
      const activeConfigs = getLayerConfigs(
        metropolisIdRef.current,
        metropolisName,
      ).filter(c => visibleRef.current[c.id])
      const outlineBefore = before
      // Move all geonet outlines just before the symbol anchor (topmost of the group)
      for (const c of activeConfigs.filter(c => c.type === 'geonet')) {
        if (m.getLayer(`${c.id}-outline`))
          m.moveLayer(`${c.id}-outline`, outlineBefore)
      }
      // The black operating-area outline stays above the hardcoded geonets.
      if (m.getLayer(OPERATING_AREA_OUTLINE_LAYER_ID))
        m.moveLayer(OPERATING_AREA_OUTLINE_LAYER_ID, outlineBefore)
      // Move all lane lines below the lowest geonet outline. Order matters:
      // each moveLayer(_, outlineBefore) lands just below outlineBefore, so the
      // last moved ends up highest. Move the PMTiles line first (bottom), then
      // the hybrid GeoJSON line on top of it (so zooming past its maxzoom reveals
      // the PMTiles beneath with no flash), then the arrows on top.
      for (const c of activeConfigs.filter(c => c.type === 'lanes')) {
        if (m.getLayer(`${c.id}-line`))
          m.moveLayer(`${c.id}-line`, outlineBefore)
        const lowLineId = laneLowLineId(c.id)
        if (m.getLayer(lowLineId)) m.moveLayer(lowLineId, outlineBefore)
        const arrowsId = laneArrowLayerId(c.id)
        if (m.getLayer(arrowsId)) m.moveLayer(arrowsId, outlineBefore)
      }
      // Move all geonet fills below lane lines
      for (const c of activeConfigs.filter(c => c.type === 'geonet')) {
        if (m.getLayer(`${c.id}-fill`))
          m.moveLayer(`${c.id}-fill`, outlineBefore)
      }

      // Re-float all active ODD layers above everything.
      for (const type of activeOddLayersRef.current) {
        const arrowLid = oddArrowLayerId(type)
        const lid = oddLayerId(type)
        if (m.getLayer(arrowLid)) m.moveLayer(arrowLid, before)
        if (m.getLayer(lid)) m.moveLayer(lid, before)
        if (m.getLayer(arrowLid) && m.getLayer(lid)) {
          m.moveLayer(arrowLid, lid)
        }
      }
    },
    [beforeLayer, metropolisName],
  )

  // The `within` clip for the currently-visible geonet zones — the union of all
  // visible geonet polygons. Lanes are filtered to this so they render only
  // inside geonet areas and appear/disappear as zones are toggled. Returns null
  // when no geonet is visible, leaving lanes unconfined.
  const currentGeonetWithin = useCallback(() => {
    const collections: FeatureCollection[] = []
    for (const c of getLayerConfigs(metropolisIdRef.current, metropolisName)) {
      if (c.type !== 'geonet' || !visibleRef.current[c.id]) continue
      const data = loadedDataRef.current[c.id]
      if (data) collections.push(data)
    }
    return buildGeonetWithinExpression(collections)
  }, [metropolisName])

  // Re-apply the current geonet clip to every visible lane's line and arrow
  // layers in place. Runs after a geonet is toggled so lanes follow their zones.
  const reapplyLaneClips = useCallback(
    (m: maplibregl.Map) => {
      const within = currentGeonetWithin()
      for (const c of getLayerConfigs(
        metropolisIdRef.current,
        metropolisName,
      )) {
        if (c.type !== 'lanes' || !visibleRef.current[c.id]) continue
        const usePmtiles =
          !!c.pmtiles?.regionId && !failedPMTilesRef.current.has(c.id)
        applyLaneGeonetClip(m, c.id, usePmtiles ? c.pmtiles : undefined, within)
      }
    },
    [currentGeonetWithin, metropolisName],
  )

  // Load a layer's static GeoJSON once and cache it (both the ref mirror — read
  // synchronously by the geonet clip — and state). Returns cached data on repeat.
  const loadLayerData = useCallback(async (config: LayerConfig) => {
    const cached = loadedDataRef.current[config.id]
    if (cached) return cached
    setLayerLoading(prev => ({ ...prev, [config.id]: true }))
    try {
      const data = await config.loader()
      loadedDataRef.current = { ...loadedDataRef.current, [config.id]: data }
      return data
    } finally {
      setLayerLoading(prev => ({ ...prev, [config.id]: false }))
    }
  }, [])

  // Render a layer from its static GeoJSON. Used directly when a layer has no
  // PMTiles config, and as the runtime fallback when a PMTiles source errors.
  const renderGeoJsonLayer = useCallback(
    async (m: maplibregl.Map, config: LayerConfig) => {
      const data = await loadLayerData(config)

      // The layer may have been toggled off while the loader was in-flight.
      if (!visibleRef.current[config.id]) return

      if (data.features.length === 0) {
        setLayerVisible(config.id, false)
        return
      }

      const before = beforeLayer(m)
      if (config.type === 'geonet') {
        addFillLayer(
          m,
          config.id,
          data,
          config.color,
          before,
          geonetFillOpacity(geonetOutlineOnlyRef.current),
        )
        // A geonet zone just changed — reclip lanes to the new zone union.
        reapplyLaneClips(m)
      } else {
        addLineLayer(
          m,
          config.id,
          data,
          config.color,
          before,
          currentGeonetWithin(),
        )
      }
      applyLayerZOrder(m)
    },
    [
      loadLayerData,
      beforeLayer,
      applyLayerZOrder,
      setLayerVisible,
      currentGeonetWithin,
      reapplyLaneClips,
    ],
  )

  const toggleLayer = useCallback(
    async (config: LayerConfig, checked: boolean) => {
      setLayerVisible(config.id, checked)
      if (!map) return

      const sourceId = `${config.id}-source`

      if (!checked) {
        pmtilesFallbackRef.current.delete(sourceId)
        removeMapLayer(
          map,
          config.id,
          config.type === 'geonet' ? 'fill' : 'line',
        )
        // Turning a geonet off shrinks the zone union — reclip remaining lanes.
        if (config.type === 'geonet') reapplyLaneClips(map)
        return
      }

      const usePmtiles =
        !!config.pmtiles?.regionId && !failedPMTilesRef.current.has(config.id)

      if (usePmtiles && config.pmtiles) {
        // Register the fallback BEFORE adding the source so a synchronous error
        // event can already find the config to fall back to.
        pmtilesFallbackRef.current.set(sourceId, config)
        const before = beforeLayer(map)
        if (config.type === 'geonet') {
          addVectorFillLayer(
            map,
            config.id,
            config.pmtiles,
            config.color,
            before,
          )
          applyLayerZOrder(map)
          return
        }
        // Hybrid lane: PMTiles renders at all zooms but sits *below* the full,
        // ungeneralized GeoJSON, which is capped at LANE_PMTILES_MIN_ZOOM (the
        // archive generalizes away most lanes at low zoom). Both confined by the
        // geonet within-clip. Zooming past the cap hides the GeoJSON and reveals
        // the already-rendered PMTiles beneath — no load flash.
        addVectorLineLayer(
          map,
          config.id,
          config.pmtiles,
          config.color,
          before,
          currentGeonetWithin(),
        )
        applyLayerZOrder(map)
        const data = await loadLayerData(config)
        // Guard: the lane may have been toggled off while the loader was in-flight.
        if (data.features.length > 0 && visibleRef.current[config.id]) {
          addGeoJsonLaneLowLayer(
            map,
            config.id,
            data,
            config.color,
            beforeLayer(map),
            currentGeonetWithin(),
            LANE_PMTILES_MIN_ZOOM,
          )
          applyLayerZOrder(map)
        }
        return
      }

      await renderGeoJsonLayer(map, config)
    },
    [
      map,
      setLayerVisible,
      beforeLayer,
      applyLayerZOrder,
      renderGeoJsonLayer,
      loadLayerData,
      currentGeonetWithin,
      reapplyLaneClips,
    ],
  )

  // Two-tier fallback: when a PMTiles vector source errors at runtime (e.g. the
  // archive is missing, unauthorized, or the source-layer doesn't exist), tear
  // it down and re-render the layer from its bundled GeoJSON. Mirrors admt-web-app.
  const renderGeoJsonLayerRef = useRef(renderGeoJsonLayer)
  renderGeoJsonLayerRef.current = renderGeoJsonLayer
  useEffect(() => {
    if (!map) return
    // MapLibre forwards each source's fired events (including 'error') to the
    // map, merging in `sourceId` — present at runtime but not part of the
    // public ErrorEvent type.
    const onError = (e: maplibregl.ErrorEvent & { sourceId?: string }) => {
      const sourceId = e.sourceId
      if (!sourceId) return
      const config = pmtilesFallbackRef.current.get(sourceId)
      if (!config) return

      pmtilesFallbackRef.current.delete(sourceId)
      failedPMTilesRef.current.add(config.id)
      removeMapLayer(map, config.id, config.type === 'geonet' ? 'fill' : 'line')
      console.warn(
        `PMTiles layer "${config.id}" failed to load; falling back to local GeoJSON.`,
        e.error,
      )
      if (visibleRef.current[config.id]) {
        void renderGeoJsonLayerRef.current(map, config)
      }
    }
    map.on('error', onError)
    return () => {
      map.off('error', onError)
    }
  }, [map])

  useEffect(() => {
    if (!map) return

    for (const type of activeOddLayersRef.current) {
      if (!visibleRef.current[`odd-${type}`]) continue

      const elements = oddLayerElementsRef.current[type]
      if (!elements) continue

      const geojson = buildOddFeatureCollection(
        type,
        elements,
        routeOddElements,
      )
      const source = map.getSource(oddSourceId(type)) as
        | maplibregl.GeoJSONSource
        | undefined

      if (source) source.setData(geojson)
    }
  }, [map, routeOddElements])

  // Auto-enable layers once per (map instance × metropolisId) combination.
  // Using a Set of "map+metro" keys so a metro change or a new map instance
  // triggers a fresh auto-enable without a ref reset race.
  // Geonets fire immediately; lanes wait for moveend so the camera transition
  // (easeTo/flyTo) finishes first and doesn't stutter under the tile load.
  // Stable ref so auto-enable effects don't re-run when loadedData changes toggleLayer identity
  const toggleLayerRef = useRef(toggleLayer)
  toggleLayerRef.current = toggleLayer

  const autoEnabledKeysRef = useRef(new Set<string>())
  const geonetsEverExpandedRef = useRef(false)
  useEffect(() => {
    if (!map || !autoEnableGeonets) return
    const key = `${metropolisId}-g`
    if (autoEnabledKeysRef.current.has(key)) return
    autoEnabledKeysRef.current.add(key)

    const run = () => {
      const configs = getLayerConfigs(metropolisId, metropolisName)
      // Metros can ship several geonets (e.g. Development + CUGT); enable only
      // the first by default so the map isn't cluttered with overlapping layers.
      const firstGeonet = configs.find(c => c.type === 'geonet')
      if (firstGeonet) toggleLayerRef.current(firstGeonet, true)
      // Only expand on first ever entry.
      if (!geonetsEverExpandedRef.current) {
        geonetsEverExpandedRef.current = true
        setExpanded(prev => ({ ...prev, geonets: true }))
      }
    }
    // The flyTo that triggers on the same navigation commit fires in a sibling
    // React effect — it hasn't called map.flyTo() yet when this effect runs, so
    // map.isMoving() is false here. We schedule on the next frame; by then MapLibre
    // has started the animation and we can use map.once('idle') to wait it out.
    let rafId: number
    let cleanup = () => {
      map.off('idle', run)
    }
    rafId = requestAnimationFrame(() => {
      if (map.isMoving()) {
        map.once('idle', run)
      } else {
        run()
        cleanup = () => {}
      }
    })
    return () => {
      cancelAnimationFrame(rafId)
      cleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, metropolisId, metropolisName, autoEnableGeonets])

  // Geonets already on the map follow the option when it changes.
  useEffect(() => {
    if (!map) return
    for (const config of getLayerConfigs(metropolisId, metropolisName)) {
      const fillId = `${config.id}-fill`
      if (config.type === 'geonet' && map.getLayer(fillId)) {
        map.setPaintProperty(
          fillId,
          'fill-opacity',
          geonetFillOpacity(geonetOutlineOnly),
        )
      }
    }
  }, [map, metropolisId, metropolisName, geonetOutlineOnly])

  const laneConfigsRef = useRef<LayerConfig[]>([])

  // Enable lanes when entering edit/create (autoEnableLanes: true)
  useEffect(() => {
    if (!map || !autoEnableLanes) return
    const key = `${metropolisId}-l`
    if (autoEnabledKeysRef.current.has(key)) return
    autoEnabledKeysRef.current.add(key)

    const run = () => {
      const configs = getLayerConfigs(metropolisId, metropolisName).filter(
        c => c.type === 'lanes',
      )
      // Metros can ship several lane sets (e.g. Development + CUGT / Production);
      // enable only the first by default. laneConfigsRef tracks what we turned on
      // so the leave-edit effect below disables exactly that.
      const firstLane = configs[0]
      laneConfigsRef.current = firstLane ? [firstLane] : []
      if (firstLane) toggleLayerRef.current(firstLane, true)
      setExpanded(prev => ({ ...prev, lanes: true }))
    }
    let rafId: number
    let cleanup = () => {
      map.off('idle', run)
    }
    rafId = requestAnimationFrame(() => {
      if (map.isMoving()) {
        map.once('idle', run)
      } else {
        run()
        cleanup = () => {}
      }
    })
    return () => {
      cancelAnimationFrame(rafId)
      cleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, metropolisId, metropolisName, autoEnableLanes])

  // Disable lanes when leaving edit/create (autoEnableLanes flips to false)
  const prevAutoEnableLanesRef = useRef(autoEnableLanes)
  useEffect(() => {
    const prev = prevAutoEnableLanesRef.current
    prevAutoEnableLanesRef.current = autoEnableLanes
    if (prev && !autoEnableLanes) {
      const key = `${metropolisId}-l`
      autoEnabledKeysRef.current.delete(key)
      for (const config of laneConfigsRef.current) {
        toggleLayerRef.current(config, false)
      }
      laneConfigsRef.current = []
      setExpanded(prev => ({ ...prev, lanes: false }))
    }
  }, [autoEnableLanes, metropolisId])

  const toggleOdd = useCallback(
    async (
      type: string,
      checked: boolean,
      preloadedElements?: Array<{
        taxonomy: string
        elementType: string | null | undefined
        location: { latitude: number; longitude: number } | null | undefined
      }>,
    ) => {
      const key = `odd-${type}`
      // Only mark as visible if we actually have a map to add the layer to.
      // Setting visible=true before the map is ready causes syncRouteOddTypes
      // to skip the type on the next call (because visibleRef already shows it on).
      if (checked && !map) return
      setLayerVisible(key, checked)
      if (!map) return

      // Supersede any in-flight request for this type: whichever toggle ran last
      // owns the state, so a slower earlier request (success or failure) must not
      // write back over it. The metro check is part of the guard, not just the
      // metro-switch effect: metropolisIdRef updates during render (and RouteMap
      // keeps the same map instance across metros), so a previous-metro response
      // could otherwise slip through before that passive effect clears the request.
      oddRequestSeqRef.current += 1
      const seq = oddRequestSeqRef.current
      const requestMetro = metropolisId
      oddCurrentRequestRef.current[type] = seq
      const isCurrent = () =>
        oddCurrentRequestRef.current[type] === seq &&
        metropolisIdRef.current === requestMetro

      if (!checked) {
        removeOddLayer(map, type)
        activeOddLayersRef.current.delete(type)
        delete oddLayerElementsRef.current[type]
        setOddLoading(prev => ({ ...prev, [type]: false }))
        return
      }

      setOddLoading(prev => ({ ...prev, [type]: true }))

      try {
        let elements: Array<{
          taxonomy: string
          elementType: string | null | undefined
          location: { latitude: number; longitude: number } | null | undefined
        }>

        if (preloadedElements) {
          elements = preloadedElements
        } else {
          // Query by enum type when possible; fall back to taxonomy filtering for
          // types not yet in the OddElementType enum (e.g. PROTECTED_LEFT/RIGHT, MERGE)
          const enumType = ODD_TYPE_TO_ELEMENT_TYPE[type]
          let queryVariables: Omit<
            OverwatchMapOddElementsPagedQueryVariables,
            'offset' | 'limit'
          >

          if (enumType) {
            queryVariables = {
              metroId: metropolisId,
              oddElementTypes: [enumType],
            }
          } else {
            const taxonomies = filterTaxonomiesByType(
              taxonomiesRef.current,
              type,
            )
            if (taxonomies.length === 0) {
              if (isCurrent()) {
                setOddCounts(prev => ({ ...prev, [type]: 0 }))
                setLayerVisible(key, false)
              }
              return
            }
            queryVariables = { metroId: metropolisId, taxonomies }
          }

          elements = await fetchAllOddElements(fetchOddPaged, queryVariables)
        }

        // Drop the result unless this is still the current request for a visible
        // layer on the current map. The visibleRef check is kept separately from
        // isCurrent() because toggleOdd(false) returns before bumping the sequence
        // when map is null, so a superseded off-toggle wouldn't be caught by
        // isCurrent() alone; the map check avoids installing on a replaced map.
        if (
          !isCurrent() ||
          !visibleRef.current[key] ||
          latestMapRef.current !== map
        )
          return

        setOddCounts(prev => ({ ...prev, [type]: elements.length }))
        oddLayerElementsRef.current[type] = elements

        // Serialize the map mutation for this type behind any earlier install so a
        // slower request can't paint its GeoJSON over a newer one. Each link
        // re-checks isCurrent/map: a superseded request skips the install and only
        // tears down a layer the current state no longer wants (a newer toggle that
        // re-enabled this type owns the shared per-type layer).
        const install = (
          oddInstallChainRef.current[type] ?? Promise.resolve()
        ).then(async () => {
          if (!isCurrent() || latestMapRef.current !== map) {
            if (!visibleRef.current[key]) removeOddLayer(map, type)
            return
          }
          const geojson = buildOddFeatureCollection(
            type,
            elements,
            routeOddElementsRef.current,
          )
          const before =
            insertBeforeLayer && map.getLayer(insertBeforeLayer)
              ? insertBeforeLayer
              : undefined
          await addOddSymbolLayer(
            map,
            type,
            geojson,
            oddIconOpacityRef.current,
            before,
          )
          if (!isCurrent() || latestMapRef.current !== map) {
            if (!visibleRef.current[key]) removeOddLayer(map, type)
            return
          }
          activeOddLayersRef.current.add(type)
        })
        // Keep the chain alive even if this install rejects; the current request's
        // own failure still surfaces through the awaited install below.
        oddInstallChainRef.current[type] = install.catch(() => {})
        await install
      } catch (error) {
        // fetchAllOddElements throws on a failed/partial paged response
        // (MAT-77839); the layer add can also throw. Only clobber state if this is
        // still the current request — otherwise a late failure would undo a newer
        // toggle. finally clears the spinner under the same guard.
        console.warn(`Failed to load ODD elements for "${type}".`, error)
        if (isCurrent()) {
          // addOddSymbolLayer builds the source and layers sequentially, so a
          // mid-way failure can leave a partial layer behind; tear it down so the
          // map matches the off state we set here rather than showing orphans.
          removeOddLayer(map, type)
          activeOddLayersRef.current.delete(type)
          delete oddLayerElementsRef.current[type]
          setLayerVisible(key, false)
          setOddCounts(prev => ({ ...prev, [type]: 0 }))
        }
      } finally {
        if (isCurrent()) {
          setOddLoading(prev => ({ ...prev, [type]: false }))
        }
      }
    },
    [map, metropolisId, fetchOddPaged, insertBeforeLayer, setLayerVisible],
  )

  const toggleAllOdd = useCallback(
    (checked: boolean) => {
      for (const { type } of ODD_TOGGLE_TYPES) {
        if ((visible[`odd-${type}`] ?? false) !== checked) {
          toggleOdd(type, checked)
        }
      }
    },
    [visible, toggleOdd],
  )

  /** Update opacity on all active ODD layers */
  const setOddOpacity = useCallback(
    (opacity: number) => {
      if (!map) return
      for (const type of activeOddLayersRef.current) {
        setOddLayerOpacity(map, type, opacity)
      }
    },
    [map],
  )

  /** Programmatically enable an ODD type (used by route builder for auto-enable) */
  const enableOddType = useCallback(
    (type: string) => {
      const key = `odd-${type}`
      if (!visibleRef.current[key]) {
        toggleOdd(type, true)
      }
    },
    [toggleOdd],
  )

  /** Programmatically disable an ODD type (used to undo VTR auto-enable) */
  const disableOddType = useCallback(
    (type: string) => {
      const key = `odd-${type}`
      if (visibleRef.current[key]) {
        toggleOdd(type, false)
      }
    },
    [toggleOdd],
  )

  /** Expand the ODD section */
  const expandOdd = useCallback(() => {
    setExpanded(prev => ({ ...prev, odd: true }))
  }, [])

  /** Collapse all overlay sections (geonets, lanes, odd) */
  const collapseAllSections = useCallback(() => {
    setExpanded({ geonets: false, lanes: false, odd: false })
  }, [])

  const toggleOddGroup = useCallback((section: string) => {
    setOddGroupExpanded(prev => ({ ...prev, [section]: !prev[section] }))
  }, [])

  /** Turn off all ODD types and clear their map layers */
  const resetAllOdd = useCallback(() => {
    for (const { type } of ODD_TOGGLE_TYPES) {
      const key = `odd-${type}`
      if (visibleRef.current[key]) toggleOdd(type, false)
    }
    autoEnabledOddRef.current.clear()
  }, [toggleOdd])

  /**
   * Reconcile the route-driven ODD layers against the types on the active route.
   *
   * `types` are the ODD types present on the current route ([] when there is no
   * route). This is the single source of truth for route-driven ODDs and it
   * diffs on every change, so — like the dim-opacity reset — route ODDs turn off
   * the moment they drop off the route or the route clears:
   *  - types no longer on the route are turned off and untracked. Manual
   *    selections are never in autoEnabledOddRef, so they're left untouched and
   *    survive route removal,
   *  - new route types that aren't already visible are turned on and tracked as
   *    route-driven.
   *
   * When `sourceElements` is provided the elements are grouped by type and passed
   * as preloaded data, bypassing the GraphQL fetch. This is used by trip-history
   * where the ODD elements come from the trip response itself.
   */
  const syncRouteOddTypes = useCallback(
    (
      types: string[],
      sourceElements?: Array<{
        type: string
        location: { latitude: number; longitude: number }
      }>,
    ) => {
      // Turn off route-driven types that are no longer on the route. Snapshot the
      // set first so we can delete from it while iterating.
      for (const type of [...autoEnabledOddRef.current]) {
        if (!types.includes(type)) {
          autoEnabledOddRef.current.delete(type)
          if (visibleRef.current[`odd-${type}`]) toggleOdd(type, false)
        }
      }

      if (types.length === 0) return

      setExpanded(prev => ({ ...prev, odd: true }))
      for (const type of types) {
        const key = `odd-${type}`
        // Already visible via a manual selection (or another driver) — leave
        // ownership with whoever turned it on; don't adopt it as route-driven.
        if (visibleRef.current[key]) continue
        if (sourceElements) {
          const preloaded = sourceElements
            .filter(el => el.type === type)
            .map(el => ({
              taxonomy: type,
              elementType: type,
              location: el.location,
            }))
          toggleOdd(type, true, preloaded)
        } else {
          toggleOdd(type, true)
        }
        autoEnabledOddRef.current.add(type)
      }
    },
    [toggleOdd],
  )

  /**
   * User-initiated ODD toggle from the sidebar. Keeps the route-driven set in
   * sync with the click so route removal behaves correctly:
   *  - turning ON a type that IS on the route keeps it route-driven — a manual
   *    click on an on-route element does not promote it to a persistent manual
   *    selection, so it still disappears when the route is removed,
   *  - turning ON a type that is NOT on the route makes it a manual selection
   *    that survives route removal (by keeping it out of the route-driven set),
   *  - turning OFF drops the type from the route-driven set.
   */
  const toggleOddManual = useCallback(
    (type: string, checked: boolean) => {
      const onRoute = routeOddElementsRef.current.some(el => el.type === type)
      if (checked && onRoute) {
        autoEnabledOddRef.current.add(type)
      } else {
        autoEnabledOddRef.current.delete(type)
      }
      toggleOdd(type, checked)
    },
    [toggleOdd],
  )

  const configs = getLayerConfigs(metropolisId, metropolisName)
  const geonetConfigs = configs.filter(c => c.type === 'geonet')
  const laneConfigs = configs.filter(c => c.type === 'lanes')

  return {
    expanded,
    toggleSection,
    oddGroupExpanded,
    toggleOddGroup,
    visible,
    oddCounts,
    oddLoading,
    layerLoading,
    zoomLevel,
    geonetConfigs,
    laneConfigs,
    toggleLayer,
    toggleOdd,
    toggleAllOdd,
    resetAllOdd,
    setOddOpacity,
    enableOddType,
    disableOddType,
    expandOdd,
    collapseAllSections,
    activeOddLayers: activeOddLayersRef,
    syncRouteOddTypes,
    toggleOddManual,
    getLoadedLayerData,
  }
}
